import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fieldWorkApi } from '../../api/field-work-api';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { compareQueueOrder, isOpenStatus } from '../../domain/fieldWorkRequest';
import { isOpenOrder } from '../../domain/fulfillment';

/** A date for a table cell; blank or invalid reads as a dash. */
export function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

/**
 * Field work at one site, in queue order. Uses the same query as the Field
 * work pages, so it is usually cached; the page and its Field work tab
 * share it.
 */
export function useSiteFieldWork(siteId: string) {
  const query = useQuery({
    queryKey: ['field-work'],
    queryFn: () => fieldWorkApi.listFieldWork(),
  });
  const requests = useMemo(
    () => (query.data ?? []).filter((request) => request.siteId === siteId).sort(compareQueueOrder),
    [query.data, siteId],
  );
  const open = useMemo(
    () => requests.filter((request) => isOpenStatus(request.status)),
    [requests],
  );
  return { query, requests, open };
}

/** Pick lists (fulfillment orders) for one site, open ones first. */
export function useSiteOrders(siteId: string) {
  const query = useQuery({
    queryKey: ['fulfillment-orders', { siteId }],
    queryFn: () => fulfillmentApi.listOrders({ siteId }),
    enabled: Boolean(siteId),
  });
  const orders = useMemo(
    () => [...(query.data ?? [])].sort((a, b) => Number(isOpenOrder(b)) - Number(isOpenOrder(a))),
    [query.data],
  );
  const open = useMemo(() => orders.filter(isOpenOrder), [orders]);
  return { query, orders, open };
}
